// File: `typescript/apps/front-end/src/main/react/page/group-page/GroupMembersToolbar.tsx`
//
// The management strip above the group members list (roadmap P2-C). Search, role filter and sort are
// visible to everyone who can see the list; the selection mode and the bulk actions are leader-only.
//
// Design language: pill controls, borders rather than shadows, one hover wash, one focus ring. The
// search field copies the /groups directory field verbatim so the two search affordances in the group
// surface look like the same control.
import React from "react";
import { ListChecks, Search, Trash2, UserX, X } from "lucide-react";
import { cn } from "@/lib/utils";
import { Localizer } from "@/main/react/Types";
import { SimpleSelect } from "@/main/react/component/SimpleSelect";
import { CtaButton } from "@/main/react/buttons/CtaButton";

export type GroupMemberRoleFilter = "all" | "leaders" | "members" | "owner" | "administrator" | "moderator";
export type GroupMemberSort = "recent" | "oldest" | "name";

export type GroupMembersFilters = {
    search: string;
    role: GroupMemberRoleFilter;
    sort: GroupMemberSort;
};

export const DEFAULT_GROUP_MEMBERS_FILTERS: GroupMembersFilters = {
    search: "",
    role: "all",
    sort: "recent",
};

export const GROUP_MEMBERS_SEARCH_DEBOUNCE_MS = 300;

export function GroupMembersToolbar({
    filters,
    onFiltersChange,
    canManageMembers,
    selectionMode,
    onSelectionModeChange,
    selectedCount,
    allVisibleSelected,
    onToggleSelectAll,
    onBulkRemove,
    onBulkBan,
    busy,
    localizer,
}: {
    filters: GroupMembersFilters;
    onFiltersChange: (next: GroupMembersFilters) => void;
    /** MODERATOR+ — gates the whole selection affordance, not just the buttons. */
    canManageMembers: boolean;
    selectionMode: boolean;
    onSelectionModeChange: (next: boolean) => void;
    selectedCount: number;
    allVisibleSelected: boolean;
    onToggleSelectAll: () => void;
    onBulkRemove: () => void;
    onBulkBan: () => void;
    busy: boolean;
    localizer: Localizer;
}) {
    // The input is local so typing stays responsive; it is debounced into the committed filter, which
    // is what actually refetches.
    const [searchDraft, setSearchDraft] = React.useState(filters.search);

    React.useEffect(() => {
        setSearchDraft(filters.search);
    }, [filters.search]);

    React.useEffect(() => {
        const trimmed = searchDraft.trim();
        if (trimmed === filters.search) {
            return;
        }
        const timeout = window.setTimeout(() => {
            onFiltersChange({ ...filters, search: trimmed });
        }, GROUP_MEMBERS_SEARCH_DEBOUNCE_MS);
        return () => window.clearTimeout(timeout);
    }, [searchDraft, filters, onFiltersChange]);

    const roleOptions = React.useMemo(
        () => [
            { key: "all", value: "all", label: localizer("group.members.manage.role.all") },
            { key: "leaders", value: "leaders", label: localizer("group.members.manage.role.leaders") },
            { key: "members", value: "members", label: localizer("group.members.manage.role.members") },
            { key: "owner", value: "owner", label: localizer("group.leadership.owner") },
            { key: "administrator", value: "administrator", label: localizer("group.leadership.administrator") },
            { key: "moderator", value: "moderator", label: localizer("group.leadership.moderator") },
        ],
        [localizer],
    );

    const sortOptions = React.useMemo(
        () => [
            { key: "recent", value: "recent", label: localizer("group.members.manage.sort.recent") },
            { key: "oldest", value: "oldest", label: localizer("group.members.manage.sort.oldest") },
            { key: "name", value: "name", label: localizer("group.members.manage.sort.name") },
        ],
        [localizer],
    );

    return (
        <div className="flex flex-col gap-3" data-playwright-id="group-members-toolbar">
            <div className="flex flex-col gap-2 sm:flex-row sm:items-center">
                <div className="relative min-w-0 flex-1">
                    <Search
                        className="pointer-events-none absolute left-3 top-1/2 size-4.5 -translate-y-1/2 text-muted-foreground"
                        aria-hidden={true}
                    />
                    <input
                        type="search"
                        value={searchDraft}
                        onChange={(event) => setSearchDraft(event.target.value)}
                        placeholder={localizer("group.members.manage.search_placeholder")}
                        aria-label={localizer("group.members.manage.search_placeholder")}
                        className={cn(
                            "w-full rounded-full border border-border bg-card py-2.5 pl-10 pr-10 text-sm [@media(pointer:coarse)]:text-base text-text-main",
                            "touch-manipulation [@media(pointer:coarse)]:min-h-11",
                            "placeholder:text-muted-foreground focus-visible:outline-none focus-visible:border-ring focus-visible:ring-[3px] focus-visible:ring-ring/50",
                        )}
                        data-playwright-id="group-members-search"
                    />
                    {searchDraft ? (
                        <button
                            type="button"
                            onClick={() => setSearchDraft("")}
                            aria-label={localizer("group.members.manage.clear_search")}
                            className="standard-hover touch-hit-44 absolute right-3 top-1/2 flex size-7 -translate-y-1/2 items-center justify-center rounded-full text-muted-foreground transition-colors focus-visible:outline-none focus-visible:border-ring focus-visible:ring-[3px] focus-visible:ring-ring/50"
                            data-playwright-id="group-members-clear-search"
                        >
                            <X className="size-4" aria-hidden={true} />
                        </button>
                    ) : null}
                </div>

                <div className="flex shrink-0 flex-wrap items-center gap-2">
                    <SimpleSelect
                        size="sm"
                        value={filters.role}
                        onValueChange={(value) => onFiltersChange({ ...filters, role: value as GroupMemberRoleFilter })}
                        options={roleOptions}
                        ariaLabel={localizer("group.members.manage.role.label")}
                        dataPlaywrightId="group-members-role-filter"
                    />
                    <SimpleSelect
                        size="sm"
                        value={filters.sort}
                        onValueChange={(value) => onFiltersChange({ ...filters, sort: value as GroupMemberSort })}
                        options={sortOptions}
                        ariaLabel={localizer("group.members.manage.sort.label")}
                        dataPlaywrightId="group-members-sort"
                    />
                    {canManageMembers ? (
                        <CtaButton
                            type="button"
                            variant={selectionMode ? "secondary" : "outline"}
                            size="sm"
                            icon={selectionMode ? X : UserX}
                            text={
                                selectionMode
                                    ? localizer("group.members.manage.select.done")
                                    : localizer("group.members.manage.select.start")
                            }
                            extraClass="rounded-full"
                            onClick={() => onSelectionModeChange(!selectionMode)}
                            playwrightId="group-members-select-toggle"
                        />
                    ) : null}
                </div>
            </div>

            {canManageMembers && selectionMode ? (
                <div
                    className="flex flex-col gap-3 rounded-xl border border-border bg-background2/40 px-3 py-2 sm:flex-row sm:flex-wrap sm:items-center sm:gap-2"
                    data-playwright-id="group-members-bulk-bar"
                >
                    <div className="flex min-w-0 flex-wrap items-center gap-3">
                        <span
                            className="text-sm font-medium text-foreground"
                            data-playwright-id="group-members-selection-count"
                        >
                            {localizer("group.members.manage.selected_count", String(selectedCount))}
                        </span>
                        <CtaButton
                            type="button"
                            variant="link"
                            size="sm"
                            icon={ListChecks}
                            text={allVisibleSelected
                                ? localizer("group.members.manage.select.none")
                                : localizer("group.members.manage.select.all")}
                            displayTextOnMobile={true}
                            onClick={onToggleSelectAll}
                            playwrightId="group-members-select-all"
                        />
                    </div>

                    <div className="flex w-full flex-col gap-2 sm:ms-auto sm:w-auto sm:flex-row sm:flex-wrap sm:items-center">
                        <CtaButton
                            type="button"
                            variant="outline"
                            size="sm"
                            icon={Trash2}
                            text={localizer("group.members.manage.bulk.remove")}
                            displayTextOnMobile={true}
                            extraClass="rounded-full w-full justify-center sm:w-auto"
                            disabled={selectedCount === 0 || busy}
                            onClick={onBulkRemove}
                            playwrightId="group-members-bulk-remove"
                        />
                        {/* The destructive action is separated from Remove rather than sitting 9px from
                            it, and it is no longer the control closest to a right thumb.

                            `danger`, not `destructive`: separating Ban is only half the intent. Left on
                            the solid variant it was a full-width red block sitting UNDER an outline
                            Remove — so the de-emphasis made the harsher of the two actions the heaviest
                            thing in the bar, which is the opposite of what was asked for. The quiet
                            tier keeps it unmistakably destructive (destructive text, tinted fill)
                            without making it the recommendation. */}
                        <div className="mt-1 border-t border-border pt-2 sm:mt-0 sm:border-0 sm:pt-0 sm:ps-2">
                            <CtaButton
                                type="button"
                                variant="danger"
                                size="sm"
                                icon={UserX}
                                text={localizer("group.members.manage.bulk.ban")}
                                displayTextOnMobile={true}
                                extraClass="rounded-full w-full justify-center sm:w-auto"
                                disabled={selectedCount === 0 || busy}
                                onClick={onBulkBan}
                                playwrightId="group-members-bulk-ban"
                            />
                        </div>
                    </div>
                </div>
            ) : null}
        </div>
    );
}

export default GroupMembersToolbar;
